import { track } from "~/modules/analytics/track";
import { AuthUserEvents } from "../analytics/events/auth";

// // // //

interface OtpResponse {
    ok: boolean;
    status: number;
    url: string;
}

/**
 * trackLogout
 * Tracks the event when a user clicks the logout button
 */
export function trackLogout() {
    track(AuthUserEvents.Logout);
}

/**
 * trackGoogleLogin
 * Tracks the event when a user clicks the "Google" social login button
 */
export function trackGoogleLogin() {
    track(AuthUserEvents.LoginLinkClickGoogle);
}

/**
 * trackLinkedInLogin
 * Tracks the event when a user clicks the "LinkedIn" social login button
 */
export function trackLinkedInLogin() {
    track(AuthUserEvents.LoginLinkClickLinkedIn);
}

/**
 * trackMicrosoftLogin
 * Tracks the event when a user clicks the "Microsoft" social login button
 */
export function trackMicrosoftLogin() {
    track(AuthUserEvents.LoginLinkClickMicrosoft);
}

/**
 * trackEmailLogin
 * Tracks the event when a user clicks the "Microsoft" social login button
 */
export function trackEmailLogin(props: { email: string }) {
    track(AuthUserEvents.EmailLogin, props);
}

/**
 * trackInlineEmailLogin
 * Tracks the event when a user clicks the "Microsoft" social login button
 */
export function trackInlineEmailLogin(props: { email: string }) {
    track(AuthUserEvents.InlineEmailLogin, props);
}

/**
 * trackEmailLoginResponseUndefined
 * Tracks the event when email login response is undefined
 */
export function trackEmailLoginResponseUndefined(props: { email: string }) {
    track(AuthUserEvents.EmailLoginResponseUndefined, props);
}

/**
 * trackInlineEmailLoginResponseUndefined
 * Tracks the event when email login response is undefined
 */
export function trackInlineEmailLoginResponseUndefined(props: {
    email: string;
}) {
    track(AuthUserEvents.InlineEmailLoginResponseUndefined, props);
}

/**
 * trackEmailLoginResponseOkFalse
 * Tracks the event when email login response.ok is false
 */
export function trackEmailLoginResponseOkFalse(props: { email: string }) {
    track(AuthUserEvents.EmailLoginResponseOkFalse, props);
}
/**
 * trackInlineEmailLoginResponseOkFalse
 * Tracks the event when email login response.ok is false
 */
export function trackInlineEmailLoginResponseOkFalse(props: { email: string }) {
    track(AuthUserEvents.InlineEmailLoginResponseOkFalse, props);
}

/**
 * trackEmailLoginSuccess
 * Tracks the event when email login is successful
 */
export function trackEmailLoginSuccess(props: { email: string }) {
    track(AuthUserEvents.EmailLoginSuccess, props);
}

/**
 * trackInlineEmailLoginSuccess
 * Tracks the event when email login is successful
 */
export function trackInlineEmailLoginSuccess(props: { email: string }) {
    track(AuthUserEvents.InlineEmailLoginSuccess, props);
}

/**
 * trackInlineOTPResponseUndefined
 * Tracks the event when OTP verification response is undefined
 */
export function trackInlineOTPResponseUndefined(props: { email: string }) {
    track(AuthUserEvents.InlineOTPResponseUndefined, props);
}

/**
 * trackOTPResponseUndefined
 * Tracks the event when OTP verification response is undefined
 */
export function trackOTPResponseUndefined(props: { email: string }) {
    track(AuthUserEvents.OTPResponseUndefined, props);
}

/**
 * trackInlineOTPResponseOkFalse
 * Tracks the event when OTP verification response.ok is false
 */
export function trackInlineOTPResponseOkFalse(props: {
    email: string;
    response: OtpResponse;
}) {
    track(AuthUserEvents.InlineOTPResponseOkFalse, props);
}

/**
 * trackOTPResponseOkFalse
 * Tracks the event when OTP verification response.ok is false
 */
export function trackOTPResponseOkFalse(props: {
    email: string;
    response: OtpResponse;
}) {
    track(AuthUserEvents.OTPResponseOkFalse, props);
}

/**
 * trackInlineOTPResponseStatusNot200
 * Tracks the event when OTP verification response status is not 200
 */
export function trackInlineOTPResponseStatusNot200(props: {
    email: string;
    response: OtpResponse;
}) {
    track(AuthUserEvents.InlineOTPResponseStatusNot200, props);
}

/**
 * trackOTPResponseStatusNot200
 * Tracks the event when OTP verification response status is not 200
 */
export function trackOTPResponseStatusNot200(props: {
    email: string;
    response: OtpResponse;
}) {
    track(AuthUserEvents.OTPResponseStatusNot200, props);
}

/**
 * trackInlineOTPResponseUrlIncludesAuthError
 * Tracks the event when OTP verification response URL includes auth error
 */
export function trackInlineOTPResponseUrlIncludesAuthError(props: {
    email: string;
    response: OtpResponse;
}) {
    track(AuthUserEvents.InlineOTPResponseUrlIncludesAuthError, props);
}

/**
 * trackOTPResponseUrlIncludesAuthError
 * Tracks the event when OTP verification response URL includes auth error
 */
export function trackOTPResponseUrlIncludesAuthError(props: {
    email: string;
    response: OtpResponse;
}) {
    track(AuthUserEvents.OTPResponseUrlIncludesAuthError, props);
}

/**
 * trackOTPSuccess
 * Tracks the event when OTP verification is successful
 */
export function trackOTPSuccess(props: { email: string }) {
    track(AuthUserEvents.OTPSuccess, props);
}

/**
 * trackGoogleRegister
 * Tracks the event when a user clicks the "Google" social login button on the register page
 */
export function trackGoogleRegister() {
    track(AuthUserEvents.RegisterLinkClickGoogle);
}

/**
 * trackInlineGoogleRegister
 * Tracks the event when a user clicks the "Google" social login button on the register page
 */
export function trackInlineGoogleRegister() {
    track(AuthUserEvents.InlineRegisterLinkClickGoogle);
}

/**
 * trackEmailRegister
 * Tracks the event when a user submits the email form on the register page
 */
export function trackEmailRegister(props: { email: string }) {
    track(AuthUserEvents.EmailRegister, props);
}

/**
 * trackEmailRegisterResponseUndefined
 * Tracks the event when email register response is undefined
 */
export function trackEmailRegisterResponseUndefined(props: { email: string }) {
    track(AuthUserEvents.EmailRegisterResponseUndefined, props);
}

/**
 * trackEmailRegisterResponseOkFalse
 * Tracks the event when email register response.ok is false
 */
export function trackEmailRegisterResponseOkFalse(props: { email: string }) {
    track(AuthUserEvents.EmailRegisterResponseOkFalse, props);
}

/**
 * trackEmailRegisterSuccess
 * Tracks the event when email register is successful
 */
export function trackEmailRegisterSuccess(props: { email: string }) {
    track(AuthUserEvents.EmailRegisterSuccess, props);
}

/**
 * trackInlineAuthenticationModalOpen
 * Tracks the event when the inline authentication modal is opened
 */
export function trackInlineAuthenticationModalOpen() {
    track(AuthUserEvents.InlineAuthenticationModalOpen);
}

/**
 * trackInlineRegistrationModalOpen
 * Tracks the event when the inline authentication modal is opened
 */
export function trackInlineRegistrationModalOpen(props: {
    label: string;
    properties: Record<string, string>;
}) {
    track(AuthUserEvents.InlineRegistrationModalOpen, {
        label: props.label,
        ...props.properties,
    });
}

/**
 * trackInlineOnboardingModalOpen
 * Tracks the event when the inline onboarding modal is opened
 */
export function trackInlineOnboardingModalOpen() {
    track(AuthUserEvents.InlineOnboardingModalOpen);
}

/**
 * trackInvitationAccept
 * Tracks the event when a user accepts a team invitation
 */
export function trackInvitationAccept(props: {
    organizationId: string;
    organizationName: string;
}) {
    track(AuthUserEvents.InvitationAccept, props);
}

/**
 * trackInvitationReject
 * Tracks the event when a user rejects a team invitation
 */
export function trackInvitationReject(props: {
    organizationId: string;
    organizationName: string;
}) {
    track(AuthUserEvents.InvitationReject, props);
}
